import get from "lodash/get";
import { ReactElement, useEffect, useState } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
  useWatch,
} from "react-hook-form";
import { StylesConfig } from "react-select";
import AsyncSelect from "react-select/async";

import { Entity } from "../../api";
import { useEntityCache } from "../../hooks/useEntityCache";
import {
  getBaseSelectStyles,
  singleSelectStyles,
} from "../../styles/react-select";
import { ErrorMessage } from "../catalyst/ErrorMessage";
import { Field } from "../catalyst/Field";
import { Label } from "../catalyst/Label";
import { IconRenderer } from "../icons/IconRenderer";

const customStyles: StylesConfig<Entity, false> = {
  ...getBaseSelectStyles<Entity, false>(),
  ...singleSelectStyles,
};

interface SelectFieldGroupProps<FormType extends FieldValues> {
  control: Control<FormType>;
  errors: FieldErrors<FormType>;
  fieldName: Path<FormType>;
  label?: string;
  onChangeEntity?: (entity: Entity | null) => void;
  placeHolder: string;
  rules?: RegisterOptions<FormType, Path<FormType>>;
  type?: string;
}

const EntitySelectField = <FormType extends FieldValues>({
  control,
  errors,
  fieldName,
  label,
  onChangeEntity,
  placeHolder,
  rules,
}: SelectFieldGroupProps<FormType>): ReactElement => {
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const { isLoading, loadEntityById, loadOptions, selectEntity } =
    useEntityCache();

  const errorObject = get(errors, fieldName);
  const errorMessage =
    typeof errorObject?.message === "string" ? errorObject.message : undefined;

  // Watch the form value at the component level
  const value = useWatch({
    control,
    name: fieldName,
  });

  // Load initial value when form value changes
  useEffect(() => {
    const loadInitialValue = async (): Promise<void> => {
      if (!value) {
        setSelectedEntity(null);
        onChangeEntity?.(null);
        return;
      }

      const entity = await loadEntityById(value);
      if (entity) {
        selectEntity(entity);
        setSelectedEntity(entity);
        onChangeEntity?.(entity);
      }
    };

    loadInitialValue();
  }, [value, loadEntityById, onChangeEntity, selectEntity]);

  return (
    <Field>
      {label && <Label className="flex items-center">{label}</Label>}
      <Controller
        control={control}
        name={fieldName}
        render={({ field: { onChange } }) => (
          <AsyncSelect<Entity, false>
            cacheOptions
            className="w-full text-white"
            defaultOptions
            formatOptionLabel={(entity) => (
              <div className="flex items-center gap-2">
                <IconRenderer className="h-6 w-6" icon={entity.icon} />
                <span>{entity.name}</span>
              </div>
            )}
            getOptionLabel={(entity) => entity.name}
            getOptionValue={(entity) => entity.id}
            isLoading={isLoading}
            loadOptions={loadOptions}
            menuPortalTarget={document.body}
            noOptionsMessage={({ inputValue }) =>
              inputValue ? "No entities found" : "Loading..."
            }
            onChange={(selected) => {
              if (selected) {
                selectEntity(selected);
              }
              onChange(selected?.id);
              setSelectedEntity(selected);
              onChangeEntity?.(selected ?? null);
            }}
            placeholder={placeHolder}
            styles={customStyles}
            value={selectedEntity}
          />
        )}
        rules={rules}
      />

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Field>
  );
};

export default EntitySelectField;
